import React, { useState, useEffect, useCallback, useMemo } from "react";
import { supabase } from "../../Services/supabaseClient";
import { useCompany } from "../../context/CompanyContext";
import { saveAs } from "file-saver";
import Papa from "papaparse";
import "./GeneralLedger.css";

const GeneralLedger = () => {
  const { companyId } = useCompany();

  // Filters (kept)
  const [filterStartDate, setFilterStartDate] = useState("");
  const [filterEndDate, setFilterEndDate] = useState("");
  const [selectedAccount, setSelectedAccount] = useState("");
  const [selectedType, setSelectedType] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");

  // NEW: sorting / grouping (added; not removing anything)
  const [sortBy, setSortBy] = useState("date"); // date | account | type | category | debit | credit
  const [sortDir, setSortDir] = useState("asc"); // asc | desc
  const [groupBy, setGroupBy] = useState("none"); // none | account | type | category

  // Data
  const [ledgerEntries, setLedgerEntries] = useState([]);
  const [filteredEntries, setFilteredEntries] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [accountTypes, setAccountTypes] = useState([]);
  const [categories, setCategories] = useState([]);

  // UI state
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  // Default date range to YTD on mount
  useEffect(() => {
    const year = new Date().getFullYear();
    setFilterStartDate(`${year}-01-01`);
    setFilterEndDate(new Date().toISOString().split("T")[0]);
  }, []);

  // ✅ Fetch GL scoped by company (JOURNAL ONLY — prevents doubling)
  const fetchGeneralLedger = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    setErr("");

    try {
      const { data: journalData, error: journalError } = await supabase
        .from("journal_entries")
        .select(
          `
          id,
          journal_id,
          account_id,
          date,
          description,
          money_in,
          money_out,
          chart_of_accounts(name, type, category)
        `
        )
        .eq("company_id", companyId)
        .order("date", { ascending: true });

      if (journalError) throw journalError;

      const formatted = (journalData || []).map((e) => ({
        id: e.id,
        journal_id: e.journal_id,
        account_id: e.account_id,
        date: e.date,
        description: e.description,
        money_in: Number(e.money_in) || 0,
        money_out: Number(e.money_out) || 0,
        chart_of_accounts: {
          name: e.chart_of_accounts?.name || "Unknown",
          type: e.chart_of_accounts?.type || "Uncategorized",
          category: e.chart_of_accounts?.category || "Uncategorized",
        },
        source: "Journal Entry",
      }));

      // Populate filter dropdowns
      setAccounts([...new Set(formatted.map((e) => e.chart_of_accounts.name))]);
      setAccountTypes([
        ...new Set(formatted.map((e) => e.chart_of_accounts.type)),
      ]);
      setCategories([
        ...new Set(formatted.map((e) => e.chart_of_accounts.category)),
      ]);

      setLedgerEntries(formatted);
      setFilteredEntries(formatted);
    } catch (e) {
      console.error("GL fetch error:", e);
      setErr("Failed to load general ledger.");
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    fetchGeneralLedger();
  }, [fetchGeneralLedger]);

  // Filtering logic (kept)
  const applyFilters = useCallback(() => {
    const filtered = ledgerEntries.filter((e) => {
      const d = new Date(e.date);
      const afterStart = !filterStartDate || d >= new Date(filterStartDate);
      const beforeEnd = !filterEndDate || d <= new Date(filterEndDate);
      const byAcct =
        !selectedAccount || e.chart_of_accounts.name === selectedAccount;
      const byType = !selectedType || e.chart_of_accounts.type === selectedType;
      const byCat =
        !selectedCategory || e.chart_of_accounts.category === selectedCategory;
      return afterStart && beforeEnd && byAcct && byType && byCat;
    });
    setFilteredEntries(filtered);
  }, [
    ledgerEntries,
    filterStartDate,
    filterEndDate,
    selectedAccount,
    selectedType,
    selectedCategory,
  ]);

  // Sorting helper
  const sortedEntries = useMemo(() => {
    const dir = sortDir === "desc" ? -1 : 1;

    const getKey = (e) => {
      switch (sortBy) {
        case "account":
          return e.chart_of_accounts.name || "";
        case "type":
          return e.chart_of_accounts.type || "";
        case "category":
          return e.chart_of_accounts.category || "";
        case "debit":
          return Number(e.money_in || 0);
        case "credit":
          return Number(e.money_out || 0);
        case "date":
        default:
          return new Date(e.date).getTime();
      }
    };

    return [...filteredEntries].sort((a, b) => {
      const ka = getKey(a);
      const kb = getKey(b);

      if (typeof ka === "number" && typeof kb === "number") {
        if (ka === kb) return String(a.id).localeCompare(String(b.id)) * dir;
        return (ka - kb) * dir;
      }

      const cmp = String(ka).localeCompare(String(kb));
      if (cmp === 0) {
        // stable fallback: date then id
        const da = new Date(a.date).getTime();
        const db = new Date(b.date).getTime();
        if (da === db) return String(a.id).localeCompare(String(b.id)) * dir;
        return (da - db) * dir;
      }
      return cmp * dir;
    });
  }, [filteredEntries, sortBy, sortDir]);

  // Compute display rows with running balance (kept)
  const displayEntries = useMemo(() => {
    // Running balance respects current sorted order
    let running = 0;
    return sortedEntries.map((e) => {
      running += e.money_in - e.money_out;
      return { ...e, balance: running };
    });
  }, [sortedEntries]);

  // Grouping rows (optional, added)
  const groupedRows = useMemo(() => {
    if (groupBy === "none") return displayEntries;

    const getGroup = (e) => {
      if (groupBy === "account") return e.chart_of_accounts.name || "Unknown";
      if (groupBy === "type")
        return e.chart_of_accounts.type || "Uncategorized";
      if (groupBy === "category")
        return e.chart_of_accounts.category || "Uncategorized";
      return "Other";
    };

    const out = [];
    let current = null;

    for (const row of displayEntries) {
      const g = getGroup(row);
      if (g !== current) {
        current = g;
        out.push({ __group: true, key: g });
      }
      out.push(row);
    }

    return out;
  }, [displayEntries, groupBy]);

  // Export CSV (kept)
  const handleExportCSV = () => {
    const exportRows = displayEntries; // export real rows only
    if (!exportRows.length) {
      alert("No data available to export.");
      return;
    }

    const csvData = exportRows.map((entry) => ({
      Date: new Date(entry.date).toLocaleDateString("en-US"),
      Account: entry.chart_of_accounts.name,
      Type: entry.chart_of_accounts.type,
      Category: entry.chart_of_accounts.category,
      Description: entry.description,
      "Debit (₦)": entry.money_in.toFixed(2),
      "Credit (₦)": entry.money_out.toFixed(2),
      "Running Balance (₦)": entry.balance.toFixed(2),
      Source: entry.source,
    }));

    const csv = Papa.unparse(csvData);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, "general_ledger.csv");
  };

  return (
    <div className="gl-container">
      <div className="gl-header">
        <div>
          <h2 className="gl-title">General Ledger</h2>
          <p className="gl-sub">Company-wide ledger · Journal-based</p>
        </div>
        <button className="gl-primary" onClick={handleExportCSV}>
          Export CSV
        </button>
      </div>

      <div className="gl-filter-card">
        <div className="gl-row">
          <div className="input-group">
            <label>Start Date</label>
            <input
              type="date"
              value={filterStartDate}
              onChange={(e) => setFilterStartDate(e.target.value)}
            />
          </div>

          <div className="input-group">
            <label>End Date</label>
            <input
              type="date"
              value={filterEndDate}
              onChange={(e) => setFilterEndDate(e.target.value)}
            />
          </div>

          <div className="input-group">
            <label>Account</label>
            <select
              value={selectedAccount}
              onChange={(e) => setSelectedAccount(e.target.value)}
            >
              <option value="">All</option>
              {accounts.map((acct) => (
                <option key={acct} value={acct}>
                  {acct}
                </option>
              ))}
            </select>
          </div>

          <div className="input-group">
            <label>Type</label>
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
            >
              <option value="">All</option>
              {accountTypes.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>

          <div className="input-group">
            <label>Category</label>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
            >
              <option value="">All</option>
              {categories.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>

          <div className="input-group">
            <label>&nbsp;</label>
            <button className="gl-primary" onClick={applyFilters}>
              Apply Filters
            </button>
          </div>
        </div>

        {/* Added controls (non-breaking) */}
        <div className="gl-row" style={{ marginTop: 10 }}>
          <div className="input-group">
            <label>Sort by</label>
            <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
              <option value="date">Date</option>
              <option value="account">Account</option>
              <option value="type">Type</option>
              <option value="category">Category</option>
              <option value="debit">Debit</option>
              <option value="credit">Credit</option>
            </select>
          </div>

          <div className="input-group">
            <label>Direction</label>
            <select
              value={sortDir}
              onChange={(e) => setSortDir(e.target.value)}
            >
              <option value="asc">Ascending</option>
              <option value="desc">Descending</option>
            </select>
          </div>

          <div className="input-group">
            <label>Group by</label>
            <select
              value={groupBy}
              onChange={(e) => setGroupBy(e.target.value)}
            >
              <option value="none">None</option>
              <option value="account">Account</option>
              <option value="type">Type</option>
              <option value="category">Category</option>
            </select>
          </div>
        </div>

        {err && <p className="gl-error">{err}</p>}
      </div>

      <div className="gl-card">
        {loading ? (
          <div className="gl-loading">Loading…</div>
        ) : (
          <div className="gl-table-wrap">
            <table className="gl-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Account</th>
                  <th>Type</th>
                  <th>Category</th>
                  <th>Description</th>
                  <th className="num">Debit (₦)</th>
                  <th className="num">Credit (₦)</th>
                  <th className="num">Balance (₦)</th>
                  <th>Source</th>
                </tr>
              </thead>

              <tbody>
                {groupedRows.length ? (
                  groupedRows.map((row) => {
                    if (row.__group) {
                      return (
                        <tr key={`grp-${row.key}`}>
                          <td colSpan="9" className="gl-group-row">
                            <strong>{row.key}</strong>
                          </td>
                        </tr>
                      );
                    }

                    const entry = row;
                    return (
                      <tr key={`${entry.source}-${entry.id}`}>
                        <td>
                          {new Date(entry.date).toLocaleDateString("en-US")}
                        </td>
                        <td>{entry.chart_of_accounts.name}</td>
                        <td>{entry.chart_of_accounts.type}</td>
                        <td>{entry.chart_of_accounts.category}</td>
                        <td className="desc">{entry.description}</td>
                        <td className="num">
                          {Number(entry.money_in).toLocaleString()}
                        </td>
                        <td className="num">
                          {Number(entry.money_out).toLocaleString()}
                        </td>
                        <td className="num">
                          {Number(entry.balance).toLocaleString()}
                        </td>
                        <td>{entry.source}</td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="9" className="empty">
                      No transactions found for the selected filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default GeneralLedger;
